import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const DashboardPage = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Loaded separately so the rest of the dashboard still shows if these fail
  const [appStats, setAppStats] = useState(null);
  const navigate = useNavigate();

  const fetchStats = async () => {
    try {
      setError(null);
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data);
      setLastUpdated(new Date());
      api.get('/admin/app-stats').then(r => setAppStats(r.data)).catch(() => setAppStats(null));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount || 0);

  const formatMoney = (amount) =>
    new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);

  const formatNumber = (num) =>
    new Intl.NumberFormat('en-ZA').format(num || 0);

  const card = ({ label, value, sub, color = '#3498db', onClick, alert = false }) => (
    <div
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: alert ? `0 2px 8px ${color}55` : '0 2px 4px rgba(0,0,0,0.1)',
        border: alert ? `2px solid ${color}` : '1px solid transparent',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.boxShadow = `0 4px 12px rgba(0,0,0,0.15)`; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.boxShadow = alert ? `0 2px 8px ${color}55` : '0 2px 4px rgba(0,0,0,0.1)'; }}
    >
      <h3 style={{ margin: '0 0 8px 0', color: '#555', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
        {onClick && <span style={{ marginLeft: '6px', fontSize: '11px', color: '#aaa' }}>↗</span>}
      </h3>
      <p style={{ fontSize: '32px', fontWeight: 'bold', color, margin: 0 }}>{value}</p>
      {sub && <p style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '5px', marginBottom: 0 }}>{sub}</p>}
    </div>
  );

  const sectionHeader = (title, description) => (
    <div style={{ margin: '32px 0 12px 0' }}>
      <h2 style={{ margin: 0, fontSize: '16px', color: '#2c3e50' }}>{title}</h2>
      {description && <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#7f8c8d' }}>{description}</p>}
    </div>
  );

  if (loading) {
    return (
      <div>
        <h1>Dashboard</h1>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginTop: '20px' }}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <div style={{ height: '12px', backgroundColor: '#ecf0f1', borderRadius: '4px', marginBottom: '12px', width: '60%' }} />
              <div style={{ height: '32px', backgroundColor: '#ecf0f1', borderRadius: '4px', width: '40%' }} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <h1>Dashboard</h1>
        <div style={{ backgroundColor: '#ffe6e6', border: '1px solid #ff4d4d', borderRadius: '8px', padding: '20px', marginTop: '20px' }}>
          <p style={{ color: '#cc0000', margin: '0 0 10px 0' }}>{error}</p>
          <button onClick={fetchStats} style={{ backgroundColor: '#3498db', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const failedPayouts = stats?.failedPayouts || 0;
  const manualPayouts = stats?.manualPayoutsRequired || 0;
  const hasPayoutAlerts = failedPayouts > 0 || manualPayouts > 0;
  const failedShipments = stats?.failedShipments || 0;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0' }}>Dashboard</h1>
          <p style={{ margin: 0, color: '#7f8c8d', fontSize: '14px' }}>Eduthrift platform overview</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <button
            onClick={fetchStats}
            style={{ backgroundColor: '#3498db', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', marginBottom: '4px' }}
          >
            Refresh
          </button>
          {lastUpdated && (
            <p style={{ fontSize: '11px', color: '#7f8c8d', margin: 0 }}>
              Updated {lastUpdated.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
      </div>

      {/* Shipment alerts banner */}
      {failedShipments > 0 && (
        <div
          onClick={() => navigate('/shipments')}
          style={{ backgroundColor: '#fdecea', border: '1px solid #e74c3c', borderRadius: '8px', padding: '14px 18px', marginTop: '20px', display: 'flex', gap: '24px', alignItems: 'center', cursor: 'pointer' }}
        >
          <span style={{ fontSize: '20px' }}>🚚</span>
          <div>
            <strong style={{ color: '#a93226' }}>{failedShipments} paid order{failedShipments > 1 ? 's' : ''} could not be booked with the courier</strong>
            <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#a93226' }}>
              Sellers can't ship until the waybill is booked. Open Shipments to see why and retry.
            </p>
          </div>
        </div>
      )}

      {/* Payout alerts banner */}
      {hasPayoutAlerts && (
        <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', padding: '14px 18px', marginTop: '20px', display: 'flex', gap: '24px', alignItems: 'center' }}>
          <span style={{ fontSize: '20px' }}>⚠️</span>
          <div>
            <strong style={{ color: '#856404' }}>Payout action required</strong>
            <p style={{ margin: '2px 0 0 0', fontSize: '13px', color: '#856404' }}>
              {failedPayouts > 0 && `${failedPayouts} failed payout${failedPayouts > 1 ? 's' : ''} need attention. `}
              {manualPayouts > 0 && `${manualPayouts} Ozow order${manualPayouts > 1 ? 's' : ''} require manual EFT to sellers.`}
            </p>
          </div>
        </div>
      )}

      {/* Users */}
      {sectionHeader('Users', 'Platform registrations and verification queue')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {card({
          label: 'Total Users',
          value: formatNumber(stats?.totalUsers),
          sub: `${formatNumber(stats?.activeUsers)} active`,
          color: '#3498db',
          onClick: () => navigate('/users'),
        })}
        {card({
          label: 'Pending Verifications',
          value: formatNumber(stats?.pendingVerifications),
          sub: 'Sellers awaiting approval',
          color: stats?.pendingVerifications > 0 ? '#e67e22' : '#27ae60',
          onClick: () => navigate('/sellers'),
          alert: stats?.pendingVerifications > 0,
        })}
        {card({
          label: 'Users with Active Listings',
          value: formatNumber(stats?.usersWithActiveListings),
          sub: `${formatNumber(stats?.activeListings)} listings up now (in stock, not expired)`,
          color: '#00aacc',
        })}
      </div>

      {/* App downloads */}
      {sectionHeader('App Downloads', 'Installs are counted the first time the app is opened on a phone. Link visits are taps on eduthrift.co.za/download and QR scans')}
      {appStats ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            {card({
              label: 'App Installs',
              value: formatNumber(appStats.installs.total),
              sub: `iPhone ${formatNumber(appStats.installs.ios)} · Android/Huawei ${formatNumber(appStats.installs.android)}`,
              color: '#00aacc',
            })}
            {card({
              label: 'New Installs',
              value: formatNumber(appStats.installs.newLast7Days),
              sub: `Last 7 days · ${formatNumber(appStats.installs.newLast30Days)} in 30 days`,
              color: '#27ae60',
            })}
            {card({
              label: 'Active Installs',
              value: formatNumber(appStats.installs.activeLast30Days),
              sub: `Opened in the last 30 days · ${formatNumber(appStats.installs.activeLast7Days)} in 7 days`,
              color: '#8e44ad',
            })}
            {card({
              label: 'Download Link Visits',
              value: formatNumber(appStats.downloadLink.total),
              sub: `${formatNumber(appStats.downloadLink.last7Days)} in 7 days · ${formatNumber(appStats.downloadLink.last30Days)} in 30 days`,
              color: '#e67e22',
            })}
            {card({
              label: 'From TikTok',
              value: formatNumber(appStats.downloadLink.bySource?.tiktok || 0),
              sub: 'Download link visits from TikTok',
              color: '#111111',
            })}
            {card({
              label: 'QR Code Scans',
              value: formatNumber(appStats.downloadLink.bySource?.qr),
              sub: 'Download link visits from the QR code',
              color: '#34495e',
            })}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginTop: '16px' }}>
            {[
              { title: 'Link visits by source', rows: Object.entries(appStats.downloadLink.bySource || {}) },
              { title: 'Link visits sent to', rows: Object.entries(appStats.downloadLink.byStore || {})
                  .map(([k, v]) => [{ ios: 'App Store', android: 'Google Play', huawei: 'AppGallery', desktop: 'Computer (shown all stores)' }[k] || k, v])
                  .sort((a, b) => b[1] - a[1]) },
            ].map(({ title, rows }) => (
              <div key={title} style={{ backgroundColor: 'white', padding: '16px 20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                <h3 style={{ margin: '0 0 8px 0', color: '#555', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h3>
                {rows.length === 0 ? (
                  <p style={{ margin: 0, fontSize: '13px', color: '#7f8c8d' }}>No visits yet</p>
                ) : rows.map(([name, count]) => (
                  <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid #f0f0f0', fontSize: '14px' }}>
                    <span style={{ color: '#2c3e50' }}>{name === 'qr' ? 'QR code' : name === 'direct' ? 'Direct (no source tag)' : name}</span>
                    <strong>{formatNumber(count)}</strong>
                  </div>
                ))}
              </div>
            ))}
          </div>
          {appStats.installs.trackedSince && (
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#7f8c8d' }}>
              Counting since {new Date(appStats.installs.trackedSince).toLocaleDateString('en-ZA')}. Phones that installed earlier are counted when they next open the app.
            </p>
          )}
        </>
      ) : (
        <p style={{ fontSize: '13px', color: '#7f8c8d' }}>App download figures are not available right now.</p>
      )}

      {/* Financial */}
      {sectionHeader('Financial', 'Sourced from the double-entry ledger — append-only, audit-grade')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {card({
          label: 'Gross Merchandise Value',
          value: formatCurrency(stats?.totalGmv),
          sub: `${formatNumber(stats?.completedOrders)} completed orders`,
          color: '#27ae60',
        })}
        {card({
          label: 'Platform Revenue (lifetime)',
          value: formatCurrency(stats?.platformRevenue),
          sub: `${formatCurrency(stats?.revenueThisMonth)} this month`,
          color: '#9b59b6',
        })}
        {card({
          label: 'Escrow Balance',
          value: formatCurrency(stats?.escrowBalance),
          sub: 'Funds currently held for buyers',
          color: '#2980b9',
        })}
        {card({
          label: 'Outstanding Seller Payouts',
          value: formatCurrency(stats?.outstandingSellerPayouts),
          sub: 'Accrued but not yet transferred',
          color: stats?.outstandingSellerPayouts > 0 ? '#e67e22' : '#27ae60',
          alert: stats?.outstandingSellerPayouts > 0,
        })}
      </div>

      {/* Live order money */}
      {sectionHeader('Orders & Money', 'Live from the orders table — every paid order, including ones not yet in the ledger')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {card({
          label: 'In Escrow Now',
          value: formatMoney(stats?.escrowHeldNow),
          sub: 'Paid by buyers (items + shipping), awaiting delivery',
          color: '#2980b9',
          onClick: () => navigate('/payments'),
        })}
        {card({
          label: 'Platform Fees Pending',
          value: formatMoney(stats?.platformFeesPending),
          sub: 'Escrow & buyer protection fees on orders still in escrow',
          color: '#9b59b6',
        })}
        {card({
          label: 'Platform Fees Earned',
          value: formatMoney(stats?.platformFeesEarned),
          sub: 'Escrow & buyer protection fees on completed orders',
          color: '#27ae60',
        })}
        {card({
          label: 'Shipping Charged',
          value: formatMoney(stats?.shippingCharged),
          sub: 'Owed back to Eduthrift for courier waybills',
          color: '#34495e',
        })}
      </div>

      {/* Shipping */}
      {sectionHeader('Shipping', 'Courier waybill bookings for paid orders')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {card({
          label: 'Failed Waybill Bookings',
          value: formatNumber(failedShipments),
          sub: failedShipments > 0 ? 'Needs attention — see reason' : 'All bookings healthy',
          color: failedShipments > 0 ? '#e74c3c' : '#27ae60',
          alert: failedShipments > 0,
          onClick: () => navigate('/shipments'),
        })}
        {card({
          label: 'Paid, Not Yet Delivered',
          value: formatNumber(stats?.paidAwaitingShipment),
          sub: 'Orders in PAYMENT_CONFIRMED',
          color: '#e67e22',
          onClick: () => navigate('/payments'),
        })}
      </div>

      {/* Payout Safety */}
      {sectionHeader('Payout Safety', 'Orders requiring intervention')}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
        {card({
          label: 'Failed Payouts',
          value: formatNumber(failedPayouts),
          sub: failedPayouts > 0 ? 'Retry logic will reattempt automatically' : 'All payouts healthy',
          color: failedPayouts > 0 ? '#e74c3c' : '#27ae60',
          alert: failedPayouts > 0,
        })}
        {card({
          label: 'Manual EFT Required',
          value: formatNumber(manualPayouts),
          sub: manualPayouts > 0 ? 'Ozow orders — manually EFT sellers' : 'Nothing pending',
          color: manualPayouts > 0 ? '#e67e22' : '#27ae60',
          alert: manualPayouts > 0,
        })}
        {card({
          label: 'Total Orders',
          value: formatNumber(stats?.totalOrders),
          sub: `${formatNumber(stats?.completedOrders)} completed`,
          color: '#34495e',
        })}
      </div>
    </div>
  );
};

export default DashboardPage;
